// Custom hook for data source operations with loading states and error handling
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { ApiError } from '../errors/ApiError';

/**
 * Enhanced hook for data source operations with loading states and error handling
 * @param {Object} dataSource - The data source object
 * @param {string} entityType - Type of entity (themes, users, questions, responses)
 * @param {string} [currentLanguage] - Current language for theme operations
 * @param {boolean} [userLanguageDetermined] - Whether user language has been determined
 * @returns {Object} Hook state and operations
 */
export function useDataSource(dataSource, entityType, currentLanguage = null, userLanguageDetermined = true) {
  const { api, state, reload, getLoadingStates } = dataSource;
  const { notify } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastOperation, setLastOperation] = useState(null);
  const operationRef = useRef(null);

  // Get current data from state
  const data = state[entityType] || [];

  // Get loading state from state manager
  const loadingStates = getLoadingStates();
  const isDataLoading = loadingStates[entityType] || false;

  /**
   * Execute operation with error handling and loading states
   */
  const executeOperation = useCallback(async (operation, ...args) => {
    const operationId = Date.now();
    operationRef.current = operationId;
    
    setIsLoading(true);
    setError(null);
    setLastOperation(operation.name);

    try {
      const result = await operation(...args);
      
      // Only update state if this is still the latest operation
      if (operationRef.current === operationId) {
        setError(null);
        return result;
      }
    } catch (err) {
      if (operationRef.current === operationId) {
        const errorMessage = err instanceof ApiError ? err.getUserMessage() : err.message;
        setError(errorMessage);
        notify({ type: 'error', message: errorMessage });
        throw err;
      }
    } finally {
      if (operationRef.current === operationId) {
        setIsLoading(false);
      }
    }
  }, [notify]);

  /**
   * Create entity
   */
  const create = useCallback(async (payload) => {
    const operation = api[`create${entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}`];
    if (!operation) {
      throw new Error(`Create operation not available for ${entityType}`);
    }
    return executeOperation(operation, payload);
  }, [api, entityType, executeOperation]);

  /**
   * Update entity
   */
  const update = useCallback(async (id, payload) => {
    const operation = api[`update${entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}`];
    if (!operation) {
      throw new Error(`Update operation not available for ${entityType}`);
    }
    return executeOperation(operation, id, payload);
  }, [api, entityType, executeOperation]);

  /**
   * Soft delete entity
   */
  const softDelete = useCallback(async (id, withChildren = false) => {
    const operation = api[`softDelete${entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}`];
    if (!operation) {
      throw new Error(`Soft delete operation not available for ${entityType}`);
    }
    return executeOperation(operation, id, withChildren);
  }, [api, entityType, executeOperation]);

  /**
   * Restore entity
   */
  const restore = useCallback(async (id) => {
    const operation = api[`restore${entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}`];
    if (!operation) {
      throw new Error(`Restore operation not available for ${entityType}`);
    }
    return executeOperation(operation, id);
  }, [api, entityType, executeOperation]);

  /**
   * Hard delete entity
   */
  const hardDelete = useCallback(async (id) => {
    const operation = api[`hardDelete${entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}`];
    if (!operation) {
      throw new Error(`Hard delete operation not available for ${entityType}`);
    }
    return executeOperation(operation, id);
  }, [api, entityType, executeOperation]);

  /**
   * Refresh data
   */
  const refresh = useCallback(async (force = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // For themes, questions, and responses, we need to pass the current language to the reload function
      if ((entityType === 'themes' || entityType === 'questions' || entityType === 'responses') && currentLanguage) {
        await reload(force ? [entityType] : null, currentLanguage);
      } else {
        await reload(force ? [entityType] : null);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.getUserMessage() : err.message;
      setError(errorMessage);
      notify({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, [reload, entityType, currentLanguage, notify]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load data on mount if not already loaded and user language is determined
  useEffect(() => {
    if (data.length === 0 && !isDataLoading && userLanguageDetermined) {
      refresh(false);
    }
  }, [data.length, isDataLoading, userLanguageDetermined, refresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      operationRef.current = null;
    };
  }, []);

  return {
    // Data
    data,
    
    // Loading states
    isLoading: isLoading || isDataLoading,
    isDataLoading,
    isOperationLoading: isLoading,
    
    // Error handling
    error,
    clearError,
    
    // Operations
    create,
    update,
    softDelete,
    restore,
    hardDelete,
    refresh,
    
    // Metadata
    lastOperation,
    hasData: data.length > 0
  };
}
